/**
 * Client log capture for support: this tab's console output, uncaught errors, network requests
 * (clientNetworkCapture: status and timing, never values), and route changes and clicks
 * (clientInteractionCapture: never text or values), batched to the backend through the
 * sender the caller gives, which tags them with the ids the support portal filters on. Used by "impersonate with logs" on the investor site and by an admin's own
 * "Capture my logs" (Settings -> Support) on the admin site; only runs on deployments where the
 * backend has CLIENT_LOG_CAPTURE_ENABLED. Stops when the sender reports the backend refused a
 * batch, or when the caller stops it.
 */

import { startInteractionCapture, stopInteractionCapture } from './clientInteractionCapture';
import { startNetworkCapture, stopNetworkCapture } from './clientNetworkCapture';

export type ClientLogLevel = 'log' | 'debug' | 'info' | 'warn' | 'error';

export interface ClientLogEntry {
  level: ClientLogLevel;
  message: string;
  ts: number;
}

/** Resolves false once the backend no longer accepts logs for this session. */
export type ClientLogSender = (entries: ClientLogEntry[]) => Promise<boolean>;

const LEVELS: ClientLogLevel[] = ['log', 'debug', 'info', 'warn', 'error'];
const FLUSH_INTERVAL_MS = 3000;
const MAX_BATCH = 100;
const MAX_QUEUE = 500;
const MAX_MESSAGE_CHARS = 2000;
// The sender's own request must never be captured, or a failing send would feed itself.
const SELF_ECHO = /(impersonations|support-logging)\/client-logs/;

interface CaptureState {
  send: ClientLogSender;
  queue: ClientLogEntry[];
  timer: ReturnType<typeof setInterval>;
  originals: Record<ClientLogLevel, (...args: unknown[]) => void>;
  onError: (event: ErrorEvent) => void;
  onRejection: (event: PromiseRejectionEvent) => void;
  sending: boolean;
}

let state: CaptureState | null = null;

export function formatLogArg(arg: unknown): string {
  if (typeof arg === 'string') return arg;
  if (arg instanceof Error)
    return `${arg.name}: ${arg.message}${arg.stack ? `\n${arg.stack}` : ''}`;
  try {
    return JSON.stringify(arg) ?? String(arg);
  } catch {
    return String(arg);
  }
}

function enqueue(level: ClientLogLevel, message: string) {
  if (!state || !message.trim() || SELF_ECHO.test(message)) return;
  if (state.queue.length >= MAX_QUEUE) state.queue.shift();
  state.queue.push({ level, message: message.slice(0, MAX_MESSAGE_CHARS), ts: Date.now() });
  if (level === 'error' || state.queue.length >= MAX_BATCH) void flushClientLogs();
}

export async function flushClientLogs(): Promise<void> {
  const current = state;
  if (!current || current.sending || current.queue.length === 0) return;
  current.sending = true;
  const batch = current.queue.splice(0, MAX_BATCH);
  try {
    const accepted = await current.send(batch);
    if (!accepted) stopClientLogCapture();
  } catch {
    // A lost batch is acceptable; reporting it through console would only be captured again.
  } finally {
    current.sending = false;
  }
}

export function isClientLogCaptureActive(): boolean {
  return state !== null;
}

export function startClientLogCapture(send: ClientLogSender): void {
  if (state) return;
  const originals = {} as CaptureState['originals'];
  LEVELS.forEach((level) => {
    originals[level] = console[level];
    console[level] = (...args: unknown[]) => {
      originals[level](...args);
      enqueue(level, args.map(formatLogArg).join(' '));
    };
  });
  const onError = (event: ErrorEvent) =>
    enqueue(
      'error',
      `Uncaught ${event.error ? formatLogArg(event.error) : event.message} (${event.filename}:${event.lineno})`,
    );
  const onRejection = (event: PromiseRejectionEvent) =>
    enqueue('error', `Unhandled rejection: ${formatLogArg(event.reason)}`);
  window.addEventListener('error', onError);
  window.addEventListener('unhandledrejection', onRejection);
  state = {
    send,
    queue: [],
    timer: setInterval(() => void flushClientLogs(), FLUSH_INTERVAL_MS),
    originals,
    onError,
    onRejection,
    sending: false,
  };
  startNetworkCapture(enqueue);
  // After state is set, since this logs the current route straight away.
  startInteractionCapture(enqueue);
}

export function stopClientLogCapture(): void {
  if (!state) return;
  const { originals, timer, onError, onRejection } = state;
  LEVELS.forEach((level) => {
    console[level] = originals[level];
  });
  clearInterval(timer);
  window.removeEventListener('error', onError);
  window.removeEventListener('unhandledrejection', onRejection);
  stopNetworkCapture();
  stopInteractionCapture();
  state = null;
}
